import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { apiConfig } from '../config/api.ts';

// Types
export interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  picture?: string;
}

export interface Prayer {
  _id: string;
  content: string;
  senderName: string | null;
  isAnonymous: boolean;
  status: 'pending' | 'approved' | 'rejected';
  reportCount: number;
  createdAt: string;
  moderationScore?: number;
  lastPrayedFor?: string;
  prayerCount?: number;
}

export interface AuthResponse {
  success: boolean;
  token: string;
  user: User;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

// API Configuration
const axiosInstance: AxiosInstance = axios.create({
  ...apiConfig,
  headers: {
    ...apiConfig.headers,
    'Content-Type': 'application/json'
  }
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const isAnonymous = localStorage.getItem('isAnonymous') === 'true';
    if (isAnonymous) {
      config.headers['x-anonymous-access'] = 'true';
    } else {
      const token = localStorage.getItem('prayerbridge_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    console.log('Making request to:', config.url, 'with data:', config.data);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('prayerbridge_refresh_token');
        if (refreshToken) {
          const response = await axiosInstance.post('/auth/refresh', { refreshToken });
          const { token } = response.data;
          localStorage.setItem('prayerbridge_token', token);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('prayerbridge_token');
        localStorage.removeItem('prayerbridge_refresh_token');
        window.location.href = '/login';
      }
    }
    console.error('API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    return Promise.reject(error);
  }
);

// API Methods
export const api = {
  baseURL: apiConfig.baseURL,

  // Auth endpoints
  auth: {
    register: (data: RegisterData): Promise<AxiosResponse<AuthResponse>> =>
      axiosInstance.post('/auth/register', data),

    login: (credentials: LoginCredentials): Promise<AxiosResponse<AuthResponse>> =>
      axiosInstance.post('/auth/login', credentials),

    logout: (): Promise<AxiosResponse<{ success: boolean }>> =>
      axiosInstance.post('/auth/logout'),

    validateToken: (): Promise<AxiosResponse<{ user: User }>> =>
      axiosInstance.get('/auth/me'),

    getProviders: (email: string): Promise<AxiosResponse<{
      providers: {
        local: boolean;
        google: boolean;
      }
    }>> => axiosInstance.get(`/auth/providers/${email}`)
  },

  // Prayer endpoints
  prayers: {
    send: (data: {
      content: string;
      senderName?: string;
      isAnonymous?: boolean;
    }): Promise<AxiosResponse<{ id: string; message: string }>> =>
      axiosInstance.post('/prayers', data),

    receive: (): Promise<AxiosResponse<Prayer>> =>
      axiosInstance.get('/prayers/receive'),

    report: (prayerId: string, reason?: string): Promise<AxiosResponse<{
      message: string;
      status: string;
    }>> => axiosInstance.post(`/prayers/${prayerId}/report`, { reason }),

    getMyPrayers: (params?: {
      page?: number;
      limit?: number;
      sort?: string;
    }): Promise<AxiosResponse<{
      prayers: Prayer[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      }
    }>> => axiosInstance.get('/prayers/my-prayers', { params })
  },

  // Admin endpoints
  admin: {
    getPrayers: (params?: {
      status?: string;
      page?: number;
      limit?: number;
      sort?: string;
    }): Promise<AxiosResponse<{
      prayers: Prayer[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      }
    }>> => axiosInstance.get('/admin/prayers', { params }),

    getStats: (): Promise<AxiosResponse<{
      total: {
        pending: number;
        approved: number;
        rejected: number;
        flagged: number;
      }
    }>> => axiosInstance.get('/admin/stats'),

    moderatePrayer: (
      prayerId: string,
      decision: 'approved' | 'rejected',
      moderationNotes?: string
    ): Promise<AxiosResponse<{
      message: string;
      status: string;
    }>> => axiosInstance.post(`/admin/prayers/${prayerId}/moderate`, {
      decision,
      moderationNotes
    })
  }
};

export default api;